import React, { useState, useEffect } from 'react';
import { 
  Briefcase, Search, Filter, Download, Eye, Edit2, Trash2, 
  X, CheckCircle, Clock, XCircle, Mail, Phone, Building2,
  GraduationCap, Users, MessageSquare, Calendar, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const InvestorConnectAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPurpose, setFilterPurpose] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Modal state
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');

  useEffect(() => {
    fetchInquiries();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [inquiries, searchQuery, filterPurpose, filterStatus, dateFrom, dateTo]);

  const fetchInquiries = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/api/admin/investor-inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(res.data.inquiries || []);
    } catch (err) {
      toast.error('Failed to fetch investor inquiries');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...inquiries];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(inq => 
        inq.name?.toLowerCase().includes(query) ||
        inq.organization?.toLowerCase().includes(query) ||
        inq.email?.toLowerCase().includes(query) ||
        inq.team?.toLowerCase().includes(query) ||
        inq.institution?.toLowerCase().includes(query)
      );
    }
    
    // Purpose filter
    if (filterPurpose !== 'all') {
      result = result.filter(inq => inq.purpose === filterPurpose);
    }
    
    // Status filter
    if (filterStatus !== 'all') {
      result = result.filter(inq => inq.status === filterStatus);
    }
    
    // Date range filter
    if (dateFrom) {
      result = result.filter(inq => new Date(inq.created_at) >= new Date(dateFrom));
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59);
      result = result.filter(inq => new Date(inq.created_at) <= toDate);
    }
    
    setFilteredInquiries(result);
  };

  const handleUpdateInquiry = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/api/admin/investor-inquiries/${selectedInquiry.id}`, {
        status: editStatus,
        notes: editNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setInquiries(prev => prev.map(inq => 
        inq.id === selectedInquiry.id 
          ? { ...inq, status: editStatus, notes: editNotes }
          : inq
      ));
      
      setShowEditModal(false);
      toast.success('Inquiry updated successfully');
    } catch (err) {
      toast.error('Failed to update inquiry');
    }
  };

  const handleDeleteInquiry = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/api/admin/investor-inquiries/${inquiryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInquiries(prev => prev.filter(inq => inq.id !== inquiryId));
      toast.success('Inquiry deleted');
    } catch (err) {
      toast.error('Failed to delete inquiry');
    }
  };

  const exportToCSV = () => {
    if (filteredInquiries.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Date', 'Investor Name', 'Organization', 'Email', 'Phone', 'Team', 'Institution', 'Purpose', 'Status', 'Proposal'];
    const rows = filteredInquiries.map(inq => [
      new Date(inq.created_at).toLocaleDateString(),
      inq.name,
      inq.organization,
      inq.email,
      inq.phone,
      inq.team,
      inq.institution || '',
      getPurposeLabel(inq.purpose),
      inq.status,
      `"${(inq.proposal || '').replace(/"/g, '""')}"`
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `investor_inquiries_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success('Exported to CSV');
  };

  const getPurposeLabel = (purpose) => {
    const labels = {
      hire: 'Hire',
      invest: 'Invest',
      mentor: 'Mentor',
      partnership: 'Partnership'
    };
    return labels[purpose] || purpose;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-medium flex items-center gap-1"><Clock size={12} /> New</span>;
      case 'contacted':
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-medium flex items-center gap-1"><Mail size={12} /> Contacted</span>;
      case 'closed':
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle size={12} /> Closed</span>;
      default:
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const getPurposeBadge = (purpose) => {
    const colors = {
      hire: 'bg-purple-500/20 text-purple-400',
      invest: 'bg-green-500/20 text-green-400',
      mentor: 'bg-blue-500/20 text-blue-400',
      partnership: 'bg-amber-500/20 text-amber-400'
    };
    return <span className={`px-2 py-1 ${colors[purpose] || 'bg-gray-500/20 text-gray-400'} rounded-full text-xs font-medium`}>
      {getPurposeLabel(purpose)}
    </span>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stats
  const stats = {
    total: inquiries.length,
    new: inquiries.filter(i => i.status === 'new').length,
    contacted: inquiries.filter(i => i.status === 'contacted').length,
    closed: inquiries.filter(i => i.status === 'closed').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="investor-connect-admin">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Briefcase className="text-purple-400" size={28} />
            Investor Connect Submissions
          </h2>
          <p className="text-gray-400 text-sm mt-1">Manage investor/recruiter inquiries from the Winners page</p>
        </div>
        
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 border border-green-500/30 rounded-lg hover:bg-green-500/30 transition-colors"
          data-testid="export-csv-btn"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">Total Inquiries</p>
          <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
        </div>
        <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
          <p className="text-blue-400 text-sm">New</p>
          <p className="text-2xl font-bold text-blue-300 mt-1">{stats.new}</p>
        </div>
        <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
          <p className="text-yellow-400 text-sm">Contacted</p>
          <p className="text-2xl font-bold text-yellow-300 mt-1">{stats.contacted}</p>
        </div>
        <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
          <p className="text-green-400 text-sm">Closed</p>
          <p className="text-2xl font-bold text-green-300 mt-1">{stats.closed}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, organization, team, institution..."
                className="w-full pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="search-input"
              />
            </div>
          </div>
          
          {/* Purpose Filter */}
          <select
            value={filterPurpose}
            onChange={(e) => setFilterPurpose(e.target.value)}
            className="px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            data-testid="purpose-filter"
          >
            <option value="all">All Purposes</option>
            <option value="hire">Hire</option>
            <option value="invest">Invest</option>
            <option value="mentor">Mentor</option>
            <option value="partnership">Partnership</option>
          </select>
          
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
          
          {/* Date Range */}
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
            <span className="text-gray-500">-</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50 border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Investor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Team / Institution</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Purpose</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {filteredInquiries.length > 0 ? filteredInquiries.map(inquiry => (
                <tr key={inquiry.id} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {formatDate(inquiry.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-white">{inquiry.name}</p>
                    <p className="text-xs text-gray-400">{inquiry.organization}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-300 flex items-center gap-1">
                      <Mail size={12} className="text-gray-500" />
                      {inquiry.email}
                    </p>
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <Phone size={12} className="text-gray-500" />
                      {inquiry.phone}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-white flex items-center gap-1">
                      <Users size={12} className="text-purple-400" />
                      {inquiry.team?.split(' - ')[0] || inquiry.team}
                    </p>
                    {inquiry.institution && (
                      <p className="text-xs text-purple-400 flex items-center gap-1 mt-0.5">
                        <GraduationCap size={12} />
                        {inquiry.institution}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getPurposeBadge(inquiry.purpose)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getStatusBadge(inquiry.status)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedInquiry(inquiry);
                          setShowDetailModal(true);
                        }}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedInquiry(inquiry);
                          setEditStatus(inquiry.status);
                          setEditNotes(inquiry.notes || '');
                          setShowEditModal(true);
                        }}
                        className="p-1.5 text-gray-400 hover:text-purple-400 hover:bg-purple-500/20 rounded transition-colors"
                        title="Edit Status"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteInquiry(inquiry.id)}
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-400">
                    <Briefcase className="mx-auto mb-3 text-gray-600" size={40} />
                    <p>No inquiries found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedInquiry && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Inquiry Details</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase">Investor Name</p>
                  <p className="text-white font-medium">{selectedInquiry.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase">Organization</p>
                  <p className="text-white font-medium">{selectedInquiry.organization}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase">Email</p>
                  <a href={`mailto:${selectedInquiry.email}`} className="text-purple-400 hover:underline">{selectedInquiry.email}</a>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase">Phone</p>
                  <a href={`tel:${selectedInquiry.phone}`} className="text-purple-400 hover:underline">{selectedInquiry.phone}</a>
                </div>
              </div>
              
              <div className="p-4 bg-gray-900/50 rounded-lg">
                <p className="text-xs text-gray-400 uppercase mb-2">Selected Team</p>
                <p className="text-white font-medium">{selectedInquiry.team}</p>
                {selectedInquiry.institution && (
                  <p className="text-purple-400 text-sm mt-1 flex items-center gap-1">
                    <GraduationCap size={14} />
                    {selectedInquiry.institution}
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase">Purpose</p>
                  {getPurposeBadge(selectedInquiry.purpose)}
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase">Status</p>
                  {getStatusBadge(selectedInquiry.status)}
                </div>
              </div>
              
              <div>
                <p className="text-xs text-gray-400 uppercase mb-2">Proposal / Expectations</p>
                <div className="p-4 bg-gray-900/50 rounded-lg">
                  <p className="text-gray-300 whitespace-pre-wrap">{selectedInquiry.proposal}</p>
                </div>
              </div>
              
              {selectedInquiry.notes && (
                <div>
                  <p className="text-xs text-gray-400 uppercase mb-2">Admin Notes</p>
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-300 whitespace-pre-wrap">{selectedInquiry.notes}</p>
                  </div>
                </div>
              )}
              
              <div className="text-xs text-gray-500">
                Submitted: {formatDate(selectedInquiry.created_at)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedInquiry && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Update Inquiry</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Inquiry from</p>
                <p className="text-white font-medium">{selectedInquiry.name} - {selectedInquiry.organization}</p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Admin Notes</label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  placeholder="Add notes about this inquiry..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 py-2 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateInquiry}
                  className="flex-1 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white font-medium transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorConnectAdmin;
